/**
 * The single owner of the `Annotation.style` JSON string (see ARCHITECTURE.md, "annotation style").
 * Every reader and writer of that string goes through the helpers below; the backend twin is
 * `bmt/annotation_style.py`. Key names must match VISUAL_ANNOTATION_KEYS and LAYOUT_ANNOTATION_KEYS
 * in `bmt/constants.py`.
 */

/** Character-level annotation style: the decoded form of `Annotation.style`. */
export interface AnnotationStyle {
  color?: string
  backgroundColor?: string
  fontSize?: string
  newlineAfter?: boolean
  stretchAfter?: boolean
  underline?: boolean
}

/** The keys that shape line layout rather than colour; they survive stripping of vowels and cantillation. */
export const LAYOUT_STYLE_KEYS = ['newlineAfter', 'stretchAfter'] as const satisfies readonly (keyof AnnotationStyle)[]

/** Decode a style string. Empty, missing, malformed or non-object input means "no style" ({}). */
export function parseAnnotationStyle(raw: string | null | undefined): AnnotationStyle {
  if (!raw) {
    return {}
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as AnnotationStyle) : {}
  } catch {
    return {}
  }
}

/** Encode a style; unset, empty and false values are dropped. A style with nothing left encodes as '' (the "delete this annotation" value). */
export function serializeAnnotationStyle(style: AnnotationStyle): string {
  const set = Object.entries(style).filter(([, value]) => value !== undefined && value !== '' && value !== false)
  return set.length ? JSON.stringify(Object.fromEntries(set)) : ''
}

/** Combine styles left to right: on a shared key the later style wins. */
export function mergeAnnotationStyles(...styles: AnnotationStyle[]): AnnotationStyle {
  return Object.assign({}, ...styles)
}

/** The truthy layout keys of a style, and nothing else. */
export function pickLayoutStyle(style: AnnotationStyle): AnnotationStyle {
  const picked: AnnotationStyle = {}
  for (const key of LAYOUT_STYLE_KEYS) {
    if (style[key]) {
      picked[key] = true
    }
  }
  return picked
}
